class BaseScrollCarousel extends HTMLElement {
    static get observedAttributes() {
        return ['disable-drag']
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue) return

        if (name === 'disable-drag') {
            this.#updateDragState()
        }
    }

    connectedCallback() {
        this.#init()

        this.#bindEvents()

        this._updateActiveState()
        this._updateStatus()
        this.#updateDragState()
    }

    disconnectedCallback() {
        this.#removeEvents()
    }

    /* ================= init ================= */

    #init = () => {
        this._scroller = this.querySelector('[data-scroller]')
        this._items = [...this.querySelectorAll('[data-scroller-item]')]

        if (!this._scroller || !this._items.length) {
            throw new Error('轮播容器或者轮播项不存在')
        }

        this._maxIndex = this._items.length - 1

        this._prevBtn = this.querySelector('[data-carousel-button][name="prev"]')
        this._nextBtn = this.querySelector('[data-carousel-button][name="next"]')

        this._indicators = [...this.querySelectorAll('[data-carousel-indicator]')]
    }

    #bindEvents = () => {
        this._scroller.addEventListener('scroll', this.#handleScroll)
        this._prevBtn?.addEventListener('click', this.#handlePrev)
        this._nextBtn?.addEventListener('click', this.#handleNext)

        this.#media.addEventListener('change', this.#updateDragState)

        this._unbindIndicators = this._bindIndicators()
    }

    #removeEvents = () => {
        this.#unregisterDrag()

        this._scroller?.removeEventListener('scroll', this.#handleScroll)
        this._prevBtn?.removeEventListener('click', this.#handlePrev)
        this._nextBtn?.removeEventListener('click', this.#handleNext)

        this.#media.removeEventListener('change', this.#updateDragState)

        this._unbindIndicators?.()
        this._unbindIndicators = null
    }

    /** ================= index ================= */

    #index = 0

    get _index() {
        return this.#index
    }

    set _index(value) {
        this.#setIndex(value)
    }

    #normalizeIndex = value => {
        const next = Number(value)
        if (!Number.isFinite(next)) return this.#index

        return Math.min(this._maxIndex, Math.max(0, next))
    }

    #setIndex = (value, { forceScroll = false, scrollOptions } = {}) => {
        const newValue = this.#normalizeIndex(value)
        const oldValue = this.#index
        const changed = newValue !== oldValue

        if (changed) {
            this.#index = newValue

            this._onIndexChanged(newValue, oldValue)
            this._updateActiveState()
        }

        if (changed || forceScroll) {
            this.#scrollToIndex(scrollOptions)
        }
    }

    _settleToIndex = (value, scrollOptions) => {
        this.#setIndex(value, {
            forceScroll: true,
            scrollOptions
        })
    }

    _onIndexChanged = (newIndex, oldIndex) => { }
    _onScrollSettled = () => { }

    /** ================= scroll 同步 ================= */

    #ticking = false
    #isProgrammaticScrolling = false

    #handleScroll = () => {
        if (this._isDragging) return

        if (!this.#ticking) {
            requestAnimationFrame(() => {
                this.#syncIndexFromScroll()
                this._updateStatus()
                this.#ticking = false
            })
            this.#ticking = true
        }
    }

    #syncIndexFromScroll = () => {
        if (this.#isProgrammaticScrolling) return
        const scrollLeft = this._scroller.scrollLeft
        const newValue = this.#normalizeIndex(this._calcIndex(scrollLeft))

        if (newValue === this.#index) return

        const oldValue = this.#index
        this.#index = newValue

        this._onIndexChanged(newValue, oldValue)
        this._updateActiveState()
    }

    /** ================= 状态 ================= */

    _updateStatus = () => {
        const { scrollWidth, clientWidth } = this._scroller
        const maxScroll = scrollWidth - clientWidth
        const canScroll = maxScroll > 0

        const progress = canScroll ? this._calcProgress() : 1

        this.style.setProperty('--progress', progress * 100 + '%')

        this._prevBtn && (this._prevBtn.disabled = !canScroll || progress <= 0 || isNaN(progress))
        this._nextBtn && (this._nextBtn.disabled = !canScroll || progress >= 1 || isNaN(progress))
    }

    _calcProgress = () => {
        const { scrollLeft, clientWidth, scrollWidth } = this._scroller
        const maxScroll = scrollWidth - clientWidth

        if (maxScroll <= 0) return 0

        // 边界兜底
        if (scrollLeft + clientWidth >= scrollWidth - 1) return 1
        if (scrollLeft <= 1) return 0

        return scrollLeft / maxScroll
    }

    /** ================= 按钮 ================= */

    #handleNext = () => this._index++
    #handlePrev = () => this._index--

    /** ================= 滚动控制 ================= */

    _scrollToIndex = options => this.#scrollToIndex(options)
    _cancelProgrammaticScroll = () => this.#cancelProgrammaticScroll()

    #scrollOperationId = 0
    #cancelScrollEndWait = null

    #scrollToIndex = ({ behavior = 'smooth', onSettled } = {}) => {
        const left = this._calcScrollLeft(this._index)

        this.#cancelProgrammaticScroll()

        const operationId = ++this.#scrollOperationId
        this.#isProgrammaticScrolling = true

        this.#cancelScrollEndWait = this.#waitScrollEnd(() => {
            if (operationId !== this.#scrollOperationId) return

            this.#cancelScrollEndWait = null
            this.#isProgrammaticScrolling = false

            this._onScrollSettled()
            onSettled?.()
        })

        this._scroller.scrollTo({
            left,
            behavior
        })
    }

    #cancelProgrammaticScroll = () => {
        this.#scrollOperationId++

        this.#cancelScrollEndWait?.()
        this.#cancelScrollEndWait = null

        if (!this.#isProgrammaticScrolling || !this._scroller) return

        const left = this._scroller.scrollLeft
        const previousScrollBehavior = this._scroller.style.scrollBehavior

        this._scroller.style.scrollBehavior = 'auto'
        this._scroller.scrollTo({ left, behavior: 'auto' })
        this._scroller.style.scrollBehavior = previousScrollBehavior

        this.#isProgrammaticScrolling = false
    }

    #waitScrollEnd = (cb) => {
        let timer = null
        let finished = false

        const cleanup = () => {
            clearTimeout(timer)
            this._scroller.removeEventListener('scroll', handleScroll)
            this._scroller.removeEventListener('scrollend', finish)
        }

        const finish = () => {
            if (finished) return

            finished = true
            cleanup()
            cb()
        }

        const scheduleFallback = () => {
            clearTimeout(timer)
            timer = setTimeout(finish, 120)
        }

        const handleScroll = () => scheduleFallback()

        this._scroller.addEventListener('scroll', handleScroll)
        this._scroller.addEventListener('scrollend', finish)

        // 即使目标位置没有变化、浏览器没有派发 scroll，也要结束等待。
        scheduleFallback()

        return () => {
            if (finished) return

            finished = true
            cleanup()
        }
    }

    /** ================= UI ================= */

    _updateActiveState = () => {
        // item
        this._items.forEach((item, index) => {
            item.classList.toggle('is-active', index === this._index)
        })

        // indicator
        this._indicators?.forEach((el, i) => {
            el.classList.toggle('is-active', i === this._index)
        })
    }

    /** ================= Drag ================= */

    #media = window.matchMedia('(min-width: 768px) and (pointer: fine)')

    get _disableDrag() {
        // 小屏直接禁用
        if (!this.#media.matches) return true

        return this.hasAttribute('disable-drag') || this._items.length <= 1
    }

    #updateDragState = () => {
        if (this._disableDrag) {
            this.#unregisterDrag()

            // 恢复浏览器默认行为
            this._scroller.style.touchAction = 'auto'
        } else {
            this.#registerDrag()

            // JS 只接管鼠标，触摸和触控笔继续使用浏览器原生滚动。
            this._scroller.style.touchAction = 'auto'
        }
    }

    #dragController = null

    get _isDragging() {
        return this.#dragController?.isDragging || false
    }

    #registerDrag = () => {
        if (this.#dragController) return

        const config = this._getDragConfig?.() || {}

        this.#dragController = createDragController({
            el: this._scroller,
            shouldStart: evt => evt.pointerType === 'mouse',
            ...config
        })

        this.#dragController.register()
    }

    #unregisterDrag = () => {
        if (!this.#dragController) return

        this.#dragController?.unregister()
        this.#dragController = null
    }

    /* ================= indicator ================= */

    _bindIndicators = () => {
        if (!this._indicators?.length) return () => { }

        const cleanups = []

        this._indicators.forEach((el, i) => {
            const handler = () => this._index = i
            el.addEventListener('click', handler)

            cleanups.push(() => el.removeEventListener('click', handler))
        })

        return () => cleanups.forEach(fn => fn())
    }

    /** ================= 抽象方法 ================= */

    _getStep() {
        throw new Error('必须实现 _getStep')
    }

    _calcIndex(scrollLeft) {
        return Math.round(scrollLeft / this._getStep())
    }

    _calcScrollLeft(index) {
        return index * this._getStep()
    }
}

class LyFullScrollCarousel extends BaseScrollCarousel {

    connectedCallback() {
        super.connectedCallback()
    }

    disconnectedCallback() {
        super.disconnectedCallback()
    }

    _getStep() {
        return this._scroller.clientWidth
    }

    _getDragConfig() {
        let startScrollLeft = 0
        let startIndex = 0
        let previousScrollBehavior = ''
        let previousScrollSnapType = ''
        let dragStarted = false

        const clampIndex = value => Math.max(0, Math.min(this._maxIndex, value))

        const restoreDragStyles = () => {
            if (!dragStarted) return

            this._scroller.style.scrollBehavior = previousScrollBehavior
            this._scroller.style.scrollSnapType = previousScrollSnapType
            dragStarted = false
        }

        const settleToIndex = index => {
            try {
                this._settleToIndex(clampIndex(index))
            } finally {
                restoreDragStyles()
            }
        }

        return {
            onStart: () => {
                previousScrollBehavior = this._scroller.style.scrollBehavior
                previousScrollSnapType = this._scroller.style.scrollSnapType
                dragStarted = true

                this._scroller.style.scrollBehavior = 'auto'
                this._scroller.style.scrollSnapType = 'none'

                // 用户开始操作时立即终止尚未完成的自动播放/按钮滚动。
                this._cancelProgrammaticScroll()

                startScrollLeft = this._scroller.scrollLeft
                startIndex = clampIndex(this._calcIndex(startScrollLeft))
            },

            onMove: ({ dx }) => {
                const next = startScrollLeft - dx

                const max = this._scroller.scrollWidth - this._scroller.clientWidth

                this._scroller.scrollLeft = Math.max(0, Math.min(max, next))
            },

            onEnd: ({ dx, velocity }) => {
                const step = this._getStep()
                const threshold = step * 0.2

                const isFast = Math.abs(velocity) > 0.5
                const isFar = Math.abs(dx) > threshold
                const direction = dx < 0 ? 1 : -1

                if (isFar || isFast) {
                    settleToIndex(startIndex + direction)
                } else {
                    settleToIndex(startIndex)
                }
            },

            onCancel: () => {
                const step = this._getStep()
                const closestIndex = Math.round(this._scroller.scrollLeft / step)

                settleToIndex(closestIndex)
            }
        }
    }
}

class LyPartScrollCarousel extends BaseScrollCarousel {

    connectedCallback() {
        super.connectedCallback()

        this.#resizeObserver = new ResizeObserver(this._onResizeCallback)
        this.#resizeObserver.observe(this._scroller)
    }

    disconnectedCallback() {
        this.#resizeObserver?.disconnect()

        super.disconnectedCallback()
    }

    #itemWidth = 0
    #resizeObserver = null

    _onResizeCallback = () => {
        const first = this._items[0]
        this._updateItemWidth(first)
        this._scrollToIndex()
    }

    _updateItemWidth = item => {

        const style = getComputedStyle(item)

        const width = parseFloat(style.width)
        const margin = parseFloat(style.marginRight)

        const scrollerStyle = getComputedStyle(this._scroller)
        let gap = parseFloat(scrollerStyle.columnGap || scrollerStyle.gap || 0)
        gap = isNaN(gap) ? 0 : gap

        const next = width + Math.max(margin, gap)

        if (next === this.#itemWidth) return

        this.#itemWidth = next
    }

    _getStep() {
        return this.#itemWidth || 1
    }

    _getDragConfig() {
        let startScrollLeft = 0

        return {
            onStart: () => {
                // 记录起点
                startScrollLeft = this._scroller.scrollLeft

                // 接管滚动
                this._scroller.style.scrollBehavior = 'auto'
                this._scroller.style.scrollSnapType = 'none'
            },
            onMove: ({ dx }) => {
                // 核心逻辑：绝对位移
                const next = startScrollLeft - dx
                this._scroller.scrollLeft = next
            },
            onEnd: ({ velocity }) => {
                // 恢复 snap
                this._scroller.style.scrollBehavior = 'smooth'
                this._scroller.style.scrollSnapType = 'x mandatory'

                const step = this._getStep()

                let targetIndex = Math.round(this._scroller.scrollLeft / step)

                const isFast = Math.abs(velocity) > 0.5

                if (isFast) {
                    targetIndex += velocity < 0 ? 1 : -1
                }

                targetIndex = Math.max(0, Math.min(this._maxIndex, targetIndex))

                this._index = targetIndex
            }
        }
    }
}

class LyVariableScrollCarousel extends BaseScrollCarousel {

    connectedCallback() {
        super.connectedCallback()

        this.#buildPositions()
        this.#resizeObserver = new ResizeObserver(this.#onResize)
        this.#resizeObserver.observe(this._scroller)
    }

    disconnectedCallback() {
        this.#resizeObserver?.disconnect()
        super.disconnectedCallback()
    }

    /* ================= positions ================= */

    #positions = []
    #resizeObserver = null

    #buildPositions = () => {
        let acc = 0
        this.#positions = this._items.map(item => {
            const pos = acc
            acc += item.offsetWidth + this.#getGap()
            return pos
        })
    }

    #getGap = () => {
        const style = getComputedStyle(this._scroller)
        const gap = parseFloat(style.columnGap || style.gap || 0)
        return isNaN(gap) ? 0 : gap
    }

    #onResize = () => {
        this.#buildPositions()
        this._scrollToIndex()
    }

    /** ================= 覆写：索引计算（二分查找） ================= */

    _calcIndex(scrollLeft) {
        const positions = this.#positions
        if (!positions.length) return 0

        let lo = 0
        let hi = positions.length - 1

        while (lo < hi) {
            const mid = (lo + hi) >>> 1
            if (positions[mid] < scrollLeft) {
                lo = mid + 1
            } else {
                hi = mid
            }
        }

        // 如果二分结果大于最后一个位置的左边界，则回退到最后一个
        if (lo > 0 && positions[lo] > scrollLeft) {
            lo--
        }

        return lo
    }

    /** ================= 覆写：滚动位置（查表） ================= */

    _calcScrollLeft(index) {
        return this.#positions[index] ?? 0
    }

    /** ================= 覆写：步长兜底 ================= */

    _getStep() {
        if (this.#positions.length < 2) return 1
        return this.#positions[1] - this.#positions[0]
    }

    /** ================= Drag ================= */

    _getDragConfig() {
        let startScrollLeft = 0

        return {
            onStart: () => {
                startScrollLeft = this._scroller.scrollLeft
                this._scroller.style.scrollBehavior = 'auto'
                this._scroller.style.scrollSnapType = 'none'
            },

            onMove: ({ dx }) => {
                const next = startScrollLeft - dx
                this._scroller.scrollLeft = next
            },

            onEnd: ({ velocity }) => {
                this._scroller.style.scrollBehavior = 'smooth'
                this._scroller.style.scrollSnapType = 'x mandatory'

                const scrollLeft = this._scroller.scrollLeft
                let targetIndex = this.#findClosestIndex(scrollLeft)

                const isFast = Math.abs(velocity) > 0.5
                if (isFast) {
                    targetIndex += velocity < 0 ? 1 : -1
                }

                targetIndex = Math.max(0, Math.min(this._maxIndex, targetIndex))
                this._index = targetIndex
            }
        }
    }

    #findClosestIndex = scrollLeft => {
        const positions = this.#positions
        if (!positions.length) return 0

        let best = 0
        let bestDist = Infinity

        for (let i = 0; i < positions.length; i++) {
            const dist = Math.abs(positions[i] - scrollLeft)
            if (dist < bestDist) {
                bestDist = dist
                best = i
            }
        }

        return best
    }
}

globalThis.BaseScrollCarousel = BaseScrollCarousel
globalThis.LyFullScrollCarousel = LyFullScrollCarousel
globalThis.LyPartScrollCarousel = LyPartScrollCarousel
globalThis.LyVariableScrollCarousel = LyVariableScrollCarousel

if (!customElements.get('ly-full-scroll-carousel')) {
    customElements.define('ly-full-scroll-carousel', LyFullScrollCarousel)
}

if (!customElements.get('ly-part-scroll-carousel')) {
    customElements.define('ly-part-scroll-carousel', LyPartScrollCarousel)
}

if (!customElements.get('ly-variable-scroll-carousel')) {
    customElements.define('ly-variable-scroll-carousel', LyVariableScrollCarousel)
}
